import { whenGuestTermsConsentSettled } from '../components/guest-terms-consent.js';
import { createModalFocusScope } from '../components/modal-focus-scope.js';

(function () {
    // Guarded so importing this module under `node --test`, where there is no document, wires nothing.
    if (globalThis.document !== undefined) {
        initGameFormRankingImpact();
    }
})();

/**
 * Makes the modal the only place a play-date change that costs ranking entries gets confirmed.
 *
 * JS owns the confirmation step and never the transport: the preview is a read of its own route, and
 * the save is always a native submit of this form. Validation errors, the success redirect and the
 * no-JS path therefore behave exactly as they do without this module, and the server still re-renders
 * its inline warning when JS is off.
 *
 * Two things reach the modal. A preview, when the dates moved since this page was rendered - the save
 * checks against the stored row, which is what the page was rendered from, so nothing else in the form
 * can change the answer. And the server's own re-rendered warning, which is what a stale confirmation
 * comes back as; it carries the same entries and the same decision, so it is adopted into the same
 * modal instead of being left a screenful above the button that acts on it.
 *
 * Neither is a cached answer. The token is re-checked against a recomputation under the row lock on
 * the save itself, so a ranking changed in another tab refreshes the warning rather than clearing
 * entries the user never saw.
 */
export function initGameFormRankingImpact(documentRef = globalThis.document) {
    const page = documentRef?.querySelector?.('[data-page-view="game-form"]');
    if (!(page instanceof HTMLElement)) {
        return;
    }

    const form = page.querySelector('[data-game-form]');
    const modal = page.querySelector('[data-game-form-ranking-modal]');
    const dialog = modal?.querySelector?.('[data-game-form-ranking-dialog]');
    const list = modal?.querySelector?.('[data-game-form-ranking-list]');
    const tokenInput = form?.querySelector?.('[data-game-form-ranking-token]');
    const confirmButton = modal?.querySelector?.('[data-game-form-ranking-confirm]');
    const submitButton = form?.querySelector?.('button[type="submit"]');

    if (
        !(form instanceof HTMLFormElement)
        || !(modal instanceof HTMLElement)
        || !(dialog instanceof HTMLElement)
        || !(list instanceof HTMLElement)
        || !(tokenInput instanceof HTMLInputElement)
        || !(confirmButton instanceof HTMLButtonElement)
        || !(submitButton instanceof HTMLButtonElement)
    ) {
        return;
    }

    const startedAtInput = form.querySelector('input[name="play_started_at"]');
    const finishedAtInput = form.querySelector('input[name="play_finished_at"]');
    const impactUrl = form.dataset.gameFormRankingImpactUrl ?? '';
    if (
        !(startedAtInput instanceof HTMLInputElement)
        || !(finishedAtInput instanceof HTMLInputElement)
        || impactUrl === ''
    ) {
        return;
    }

    const currentDates = () => `${startedAtInput.value}|${finishedAtInput.value}`;
    const renderedDates = currentDates();
    // Not the button's own text: the two-phase form renders it as `Save and clear ranking entries` when
    // it is the no-JS confirmation, and here it is only ever the trigger.
    const idleLabel = submitButton.dataset.gameFormSubmitLabel ?? submitButton.textContent;
    // The dates the loaded token answers for. Non-null means a confirmation is pending and the modal,
    // not the network, is what the next Save has to go through.
    let answeredDates = null;
    let checking = false;
    let saving = false;
    // One-shot permission for the submit this module is itself starting. Consumed by the handler, so a
    // later click cannot ride the same flag through.
    let allowNextSubmit = false;

    const closeControls = modal.querySelectorAll('[data-game-form-ranking-close]');

    const closeModal = () => {
        // A confirmed save is already on the wire and cannot be called back. The modal going away is
        // how this screen says an action did not happen, so while one is happening it stays.
        if (saving || modal.hidden) {
            return;
        }

        modal.hidden = true;
        focusScope.deactivate();
    };

    const focusScope = createModalFocusScope({ dialog, requestClose: closeModal, documentRef });
    if (focusScope === null) {
        return;
    }

    const clearConfirmation = () => {
        tokenInput.value = '';
        answeredDates = null;
    };

    const openModal = () => {
        modal.hidden = false;
        focusScope.activate(submitButton);
        focusScope.focus();
    };

    const setChecking = (busy) => {
        checking = busy;
        submitButton.disabled = busy;
        submitButton.textContent = busy ? 'Checking rankings...' : idleLabel;
    };

    /**
     * Shuts both ways into the form for the submit that is now under way. The submit event has already
     * fired, so disabling here cannot cancel it - and the page stays interactive until the browser
     * navigates, which is the window a second click would post through. That second POST is not a
     * harmless repeat: `updateGame()` publishes `GameUpdatedEvent` unconditionally, and the guest
     * mutation count is decided from a read taken before the row lock, so two crossing saves both
     * count.
     */
    const beginSave = () => {
        saving = true;
        submitButton.disabled = true;
        confirmButton.disabled = true;
        // A modal that refuses to close and says nothing is worse than one that closes wrongly, so the
        // control that is now inert names what it is waiting for.
        confirmButton.textContent = 'Saving...';
        closeControls.forEach((control) => {
            control.disabled = true;
        });
    };

    const requestImpact = async () => {
        const query = new URLSearchParams({
            started_at: startedAtInput.value,
            finished_at: finishedAtInput.value,
        });

        try {
            const response = await fetch(`${impactUrl}?${query}`, { headers: { Accept: 'application/json' } });

            return response.ok ? await response.json() : null;
        } catch {
            return null;
        }
    };

    const runPreview = async () => {
        // From here until an answer arrives there is no confirmation, and the form must not carry one.
        // The token is a digest of an impact set, not of the dates it was computed for, so a set that
        // disappears and comes back recomputes to the same digest - a token left over from a cancelled
        // preview could authorize clearing entries this form never displayed.
        clearConfirmation();
        setChecking(true);

        let askedFor = '';
        let impact = null;
        // Re-asks when the dates moved while the answer was in flight: that answer describes a form
        // that no longer exists, and acting on it is the same staleness. Each turn is a round trip, so
        // this settles as soon as the user stops editing.
        do {
            askedFor = currentDates();
            impact = await requestImpact();
        } while (impact !== null && currentDates() !== askedFor);

        setChecking(false);

        // Nothing to confirm, or the preview did not come back (offline, a validation 422, a session
        // that expired into a redirect). Submit for real, now carrying no token, and let the server
        // answer - it re-checks anyway, and its answer without JS is this same form with the warning on
        // it.
        const entries = Array.isArray(impact?.entries) ? impact.entries : [];
        if (entries.length === 0) {
            allowNextSubmit = true;
            form.requestSubmit();

            return;
        }

        tokenInput.value = typeof impact.token === 'string' ? impact.token : '';
        answeredDates = askedFor;
        list.replaceChildren(...entries.map((entry) => {
            const item = documentRef.createElement('li');
            item.textContent = `${entry.ranking} – ${entry.entry}`;

            return item;
        }));
        openModal();
    };

    form.addEventListener('submit', (event) => {
        if (saving) {
            event.preventDefault();

            return;
        }

        if (allowNextSubmit) {
            allowNextSubmit = false;

            // A field went invalid while the preview was in flight, so this submit is being cancelled -
            // by `game-form-validation.js` ahead of this listener, and by the second check whatever the
            // listener order turns out to be. Nothing is being saved, so nothing may be locked, and the
            // modal has to come off the field the user now has to fix.
            if (event.defaultPrevented || !form.checkValidity()) {
                // The validator has already put focus on the field that has to be fixed. The modal's
                // own focus restoration aims at the button that opened it, which would take the user
                // straight back off the thing they now have to deal with.
                const focusedField = documentRef.activeElement;
                closeModal();
                if (focusedField instanceof HTMLElement && form.contains(focusedField)) {
                    focusedField.focus();
                }

                return;
            }

            beginSave();

            return;
        }

        if (checking) {
            event.preventDefault();

            return;
        }

        // `game-form-validation.js` owns the field errors and has already blocked this submit; asking
        // the server about dates it is going to reject would only put a modal over them.
        if (!form.checkValidity()) {
            return;
        }

        const dates = currentDates();
        if (dates === answeredDates) {
            event.preventDefault();
            openModal();

            return;
        }

        if (dates === renderedDates) {
            beginSave();

            return;
        }

        event.preventDefault();
        void runPreview();
    });

    confirmButton.addEventListener('click', () => {
        allowNextSubmit = true;
    });

    closeControls.forEach((button) => {
        button.addEventListener('click', () => closeModal());
    });

    adoptServerRenderedWarning();

    /**
     * The two-phase form's own warning, rendered because the confirmation came back stale or because
     * the first phase ran without this module. Its entries move into the modal and the inline block is
     * hidden, so there is one warning on the page and one place to answer it.
     */
    function adoptServerRenderedWarning() {
        const warning = form.querySelector('[data-game-form-ranking-impact]');
        const warningList = warning?.querySelector?.('[data-game-form-ranking-impact-list]');
        if (!(warning instanceof HTMLElement) || !(warningList instanceof HTMLElement)) {
            return;
        }

        list.replaceChildren(...warningList.children);
        warning.hidden = true;
        answeredDates = renderedDates;
        // The destructive confirmation now lives in the modal, so the trigger goes back to naming what
        // it does - which, with this loaded, is reopening the modal rather than clearing anything.
        submitButton.textContent = idleLabel;
        whenGuestTermsConsentSettled(openModal, documentRef);
    }
}
